import { AppContext } from "App";

import { ICultureClient } from "@multiweb/clients/culture";
import { ClientType, IClientFactory } from "@multiweb/clients/index";
import { ISectionLoggerFactory, LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ILanguageExtended } from "@multiweb/types/contracts";
import { CancellationToken } from "@multiweb/types/multiweb";
import { registerDefaultLogging } from "Logging/defaultRegistrator";
import { DataEventsManager } from "OW.Remoting/Remoting/DataEventsManager";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { ErrorHandler, IErrorHandler, StandardErrorHandler } from "UI/Errors";
import { DIContainer } from "../../../../Global/Base/DIContainer";
import { InvalidArgumentException } from "../../../../Global/Base/Exceptions";
import { ApplicationStorage, AppStorageItemType } from "../../../../OW.Remoting/Remoting/ApplicationStorage";
import { AppMemCache } from "../../../../OW.Remoting/Remoting/AppMemCache";
import { IAuthManager } from "../../../../OW.Remoting/Remoting/Auth/IAuthManager";
import { StandardAuthManager } from "../../../../OW.Remoting/Remoting/Auth/StandardAuthManager";
import { IModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { StandardModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IAppCache } from "../../../../OW.Remoting/Remoting/Cache/IAppCache";
import { CheckBox } from "../../UI/Controls/CheckBox";
import { ComboBox } from "../../UI/Controls/ComboBox";
import { Edit } from "../../UI/Controls/Edit";
import { PasswordEdit } from "../../UI/Controls/PasswordEdit";
import { Registrator } from "../../UI/Controls/Registrator";
import { StringBuilder } from "../../UI/DataBuilders/StringBuilder";
import { IOpenedFormsHistoryManager } from "../../UI/Helpers/IOpenedFormsHistoryManager";
import { OpenedFormsHistoryManager } from "../../UI/Helpers/OpenedFormsHistoryManager";
import { ISystemOperationRights, SystemOperationRights } from "../../UI/Helpers/SystemOperationRights";
import { MessageWindow } from "../../UI/MessageWindow";
import { IUIManagerWorker } from "../../UI/UIManager";
import { AlignPanel } from "../../UI/XGControls/AlignPanel";
import { DefUIManagerWorker } from "../DefUIManagerWorker";
import { EmailForm } from "./EmailForm";
import { LoginForm } from "./LoginForm";
import { LoginStorage } from "./LoginStorage";
import { NavigationService } from "./NavigationService";
import { SmsForm } from "./SmsForm";
import { TotpForm } from "./TotpForm";

/**
 * Manages and coordinates the whole process of logging in.
 */
export class LoginManager {
    private _loginFormElement: HTMLElement;
    private _loginForm: LoginForm;
    private _smsForm: SmsForm;
    private _emailForm: EmailForm;
    private _totpForm: TotpForm;
    private _navigationService = new NavigationService((formName: string) => this._onFormChanged(this, formName));
    private _loginWatchHandle: number | undefined;
    private _initialAuthId: string | undefined;
    private _cultureClient: ICultureClient;

    constructor(loginFormElement: HTMLElement) {
        if (!loginFormElement)
            throw new InvalidArgumentException("loginFormElement");
        this._loginFormElement = loginFormElement;

        this._initAuthId();
    }

    public async initializeComponents(): Promise<void> {
        await this._registerComponentsAndTypes();

        ErrorHandler.instance.activateAsyncGlobalErrorHandler();

        const sectionLoggerFactory = DIContainer.instance.create(ISectionLoggerFactory);
        this._cultureClient = DIContainer.instance.create(IClientFactory).get(ClientType.culture);
        const languages = await this._cultureClient.getSupportedLanguages();
        this._initializeLanguage(languages);

        await Registrator.initializeControls(this._loginFormElement);
        const authManager = DIContainer.instance.create<IAuthManager>(IAuthManager);

        this._loginForm = new LoginForm(
            authManager,
            this._navigationService,
            languages,
            () => this._onSuccessfulLogin(this),
            AppContext.instance.configuration.defaultDomain,
            sectionLoggerFactory.create(LoggingSection.authentication),
        );
        this._smsForm = new SmsForm(this._loginForm, authManager, this._navigationService, () => this._onSuccessfulLogin(this));
        this._emailForm = new EmailForm(this._loginForm, authManager, this._navigationService, () => this._onSuccessfulLogin(this));
        this._totpForm = new TotpForm(this._loginForm, authManager, this._navigationService, () => this._onSuccessfulLogin(this));
    }

    private _initializeLanguage(languages: readonly ILanguageExtended[]): void {
        const lastUsedLanguage = LoginStorage.getLastSelectedLoginLang();
        let langInfo = lastUsedLanguage ? languages.find(lang => lang.code === lastUsedLanguage) : null;
        if (!langInfo)
            langInfo = languages.find(lang => lang.isPrimary);

        Localization.setLanguage(langInfo.code, langInfo.culture);
    }

    private async _initAuthId(): Promise<void> {
        // sledování přihlášení v jiných oknech
        this._initialAuthId = ApplicationStorage.get(`SpecKey-${AppContext.instance.appId}`);

        this._startWatchingLogin();
    }

    private _stopWatchingLogin(): void {
        if (this._loginWatchHandle !== undefined) {
            clearInterval(this._loginWatchHandle);
            this._loginWatchHandle = undefined;
        }
    }

    private async _startWatchingLogin(): Promise<void> {
        this._loginWatchHandle = self.setInterval(() => {
            if (this._initialAuthId != ApplicationStorage.get(`SpecKey-${AppContext.instance.appId}`)) {
                this._stopWatchingLogin();
                location.href = `main${location.search}`; // `
            }
        }, 1000);
    }

    private async _registerComponentsAndTypes(): Promise<void> {
        const reg: typeof Registrator = Registrator;
        reg.add("AlignPanel", AlignPanel);
        reg.add("ComboBox", ComboBox);
        reg.add("Edit", Edit);
        reg.add("PasswordEdit", PasswordEdit);
        reg.add("CheckBox", CheckBox);

        const context = AppContext.instance;
        const configuration = context.configuration;
        const di = DIContainer.instance;
        const clientFactory = di.create(IClientFactory);
        const createLogger = (section: LoggingSection) => DIContainer.instance.create(ISectionLoggerFactory).create(section);

        registerDefaultLogging(configuration.logging);
        LazyLoadingSectionLogger.initDependencies(di.create(ISectionLoggerFactory));

        di.registerFactory(IOpenedFormsHistoryManager,
            () => new OpenedFormsHistoryManager(
                clientFactory.get(ClientType.userSettings),
                configuration.openedFormsHistoryCount,
            ), true);
        di.registerFactory(IUIManagerWorker,
            () => new DefUIManagerWorker(
                document.getElementById("login-dialog"),
                document.getElementById("loader"),
                0,
                null,
                di.create(IDataEventsManager),
            ), true);
        di.registerFactory(IErrorHandler,
            () => new StandardErrorHandler(
                !!configuration.developmentMode,
                di.create(IUIManagerWorker),
                di.create(ISectionLoggerFactory).create(LoggingSection.coreUI),
            ), true);
        di.registerFactory(IAppCache, () => new AppMemCache(), true);
        di.registerFactory(IModelDescriptionsManager, () => new StandardModelDescriptionsManager(createLogger(LoggingSection.modelDescription)), true);
        di.registerFactory(ISystemOperationRights, () => new SystemOperationRights(di.create(ICommunicationManager), createLogger(LoggingSection.rights)), true);
        di.registerFactory(IDataEventsManager, () => new DataEventsManager(clientFactory.get(ClientType.dataEvents), createLogger(LoggingSection.dataEvents)), true);

        di.registerFactory(IAuthManager,
            () => new StandardAuthManager(
                clientFactory.get(ClientType.authentication),
                clientFactory.get(ClientType.user),
                configuration.clientCode,
                di.create(ISectionLoggerFactory).create(LoggingSection.authentication),
            ), true);
    }

    /**
     * Information about the current user is saved into the local storage
     */
    private _onSuccessfulLogin(loginManager: LoginManager): void {
        try {
            ApplicationStorage.set(AppStorageItemType.winAuthUsed, false.toString());
            LoginStorage.setLastLoginUsername(loginManager._loginForm.getUsername(false));
            this._stopWatchingLogin();
            loginManager._cultureClient.saveLanguageProfile(loginManager._loginForm.getLanguage(), CancellationToken.none).finally(() => {
                location.href = `main${location.search}`;
            });
        } catch (exp) {
            MessageWindow.showAlert(new StringBuilder(exp.toString()));
        }
    }

    /**
     * Gets invoked when a view is navigated to a form, in other words the visible form has been changed.
     * @param loginManager Instance of this class.
     * @param changedToFormName A name of the form to which the view has been navigated.
     */
    private _onFormChanged(loginManager: LoginManager, changedToFormName: string): void {
        if (changedToFormName === SmsForm.name) {
            loginManager._smsForm.initialize();
        } else if (changedToFormName === EmailForm.name) {
            loginManager._emailForm.initialize();
        } else if (changedToFormName === TotpForm.name) {
            loginManager._totpForm.initialize();
        }
    }
}
